/**
 * The reason a session ended, handed from the refresh that failed to the login
 * page the redirect lands on.
 *
 * `clear()` wipes the auth store and the navigation that follows unmounts
 * whatever was on screen, so at the moment a session actually dies there is no
 * live component left to toast from. sessionStorage is the only channel that
 * survives that hop, and being per-tab it does not announce a logout to a tab
 * that did not have one.
 *
 * Code and extensions travel under separate keys: the code keeps the plain
 * string shape the key name promises, and a refusal that carries no figures
 * writes nothing at all rather than an empty envelope.
 */
import { ApiError } from './error.js';
import { storageKey } from '../storage-key.js';

const CODE_KEY = 'lastAuthErrorCode';
const EXTENSIONS_KEY = 'lastAuthErrorExtensions';

function parseExtensions(raw: string | null): Record<string, unknown> | undefined {
  if (!raw) return undefined;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return undefined;
    return parsed as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

/**
 * Records a refusal that is about to end the session. A code-less refusal is
 * not recorded: the login page has nothing to say about it beyond the generic,
 * and a stale entry would then outlive the session it described.
 */
export function stashForcedLogout(err: ApiError): void {
  if (!err.code) return;
  sessionStorage.setItem(storageKey(CODE_KEY), err.code);
  if (err.extensions) {
    sessionStorage.setItem(storageKey(EXTENSIONS_KEY), JSON.stringify(err.extensions));
  } else {
    // A previous stash's figures must not attach themselves to this code.
    sessionStorage.removeItem(storageKey(EXTENSIONS_KEY));
  }
}

/**
 * Reads the recorded refusal and clears it, so a reload of the login page does
 * not repeat a notice about a session that is already gone. Shaped as an
 * `ApiError` so the caller feeds it to the same catalog every other refusal
 * goes through.
 */
export function consumeForcedLogout(): ApiError | null {
  const code = sessionStorage.getItem(storageKey(CODE_KEY));
  const extensions = parseExtensions(sessionStorage.getItem(storageKey(EXTENSIONS_KEY)));
  sessionStorage.removeItem(storageKey(CODE_KEY));
  sessionStorage.removeItem(storageKey(EXTENSIONS_KEY));
  if (!code) return null;
  return new ApiError({ status: 401, code, message: 'session expired', extensions });
}
